import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

function AddCase() {
	const { userRole } = useAuth();
	const navigate = useNavigate();

	const [name, setName] = useState("");
	const [weight, setWeight] = useState("");
	const [notes, setNotes] = useState("");
	const [imageFile, setImageFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(null);
	const [kits, setKits] = useState([]);
	const [selectedKitId, setSelectedKitId] = useState("");

	async function uploadImage(file) {
		if (!file) return null;
		const fileExt = file.name.split(".").pop();
		const fileName = `${Date.now()}.${fileExt}`;
		const filePath = `cases/${fileName}`;

		const { error: uploadError } = await supabase.storage
			.from("case-images")
			.upload(filePath, file);

		if (uploadError) {
			setError("Image upload failed");
			console.error(uploadError);
			return null;
		}
		const { data } = supabase.storage
			.from("case-images")
			.getPublicUrl(filePath);
		return data.publicUrl;
	}

	useEffect(() => {
		const fetchKits = async () => {
			const { data, error } = await supabase
				.from("kits")
				.select("id, name")
				.order("name");

			if (error) {
				console.error("Error fetching kits:", error);
			} else {
				setKits(data);
			}
		};

		fetchKits();
	}, []);

	async function handleSubmit(e) {
		e.preventDefault();
		setError(null);

		if (!name.trim()) {
			setError("Name is required");
			return;
		}

		setUploading(true);

		let imageUrl = null;
		if (imageFile && userRole === "admin") {
			imageUrl = await uploadImage(imageFile);
			if (!imageUrl) {
				setUploading(false);
				return;
			}
		}

		// 1. Insert case (without kit_id)
		const { data: insertedCase, error: insertError } = await supabase
			.from("cases")
			.insert([
				{
					name,
					weight: weight ? Number(weight) : null,
					notes,
					image_url: imageUrl,
				},
			])
			.select()
			.single(); // So we can get the new case's ID

		if (insertError) {
			setError("Failed to add case");
			console.error(insertError);
			setUploading(false);
			return;
		}

		// 2. If a kit was selected, insert into kit_cases
		if (selectedKitId) {
			const { error: linkError } = await supabase
				.from("kit_cases")
				.insert([
					{
						case_id: insertedCase.id,
						kit_id: selectedKitId,
					},
				]);

			if (linkError) {
				setError("Case added, but failed to associate with kit");
				console.error(linkError);
				setUploading(false);
				return;
			}
		}

		setUploading(false);
		navigate(`/cases/${insertedCase.id}`);
	}

	return (
		<div className="main add-item-page">
			<h1 className="add-item__title">Add New Case</h1>

			{error && <div className="add-item__error">{error}</div>}

			<form className="add-item__form" onSubmit={handleSubmit}>
				<div className="form-group">
					<label htmlFor="name">Name (required)</label>
					<input
						type="text"
						id="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
					/>
				</div>

				<div className="form-group">
					<label htmlFor="weight">Weight (lbs)</label>
					<input
						type="number"
						id="weight"
						value={weight}
						onChange={(e) => setWeight(e.target.value)}
						min="0"
						step="any"
					/>
				</div>

				<div className="form-group">
					<label htmlFor="kit">Assign to Kit</label>
					<select
						id="kit"
						value={selectedKitId}
						onChange={(e) => setSelectedKitId(e.target.value)}
					>
						<option value="">-- None --</option>
						{kits.map((kit) => (
							<option key={kit.id} value={kit.id}>
								{kit.name}
							</option>
						))}
					</select>
				</div>

				<div className="form-group">
					<label htmlFor="notes">Notes</label>
					<textarea
						id="notes"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
				</div>

				{userRole === "admin" && (
					<div className="form-group">
						<label htmlFor="image">Image</label>
						<input
							type="file"
							id="image"
							accept="image/*"
							onChange={(e) => setImageFile(e.target.files[0])}
						/>
					</div>
				)}

				<div className="form-actions">
					<button
						type="submit"
						disabled={uploading}
						className="button green"
					>
						{uploading ? (
							"Adding..."
						) : (
							<>
								<i className="pi pi-plus"></i>Add Case
							</>
						)}
					</button>
					<button
						type="button"
						className="button"
						onClick={() => navigate("/")}
					>
						Cancel
					</button>
				</div>
			</form>
		</div>
	);
}

export default AddCase;
